import { Menu, MenuStatus, MenuType, MenuCycle } from '../../app/services/api/menus/menusTypes';

/** Seed data for the Range Planning "Menus" demo screen (src/pages/Menus) - derived from the
 * Thomas Franks "The British Kitchen" premium menu dataset (single range, single menu). The
 * one menu here ("Autumn at the Table", id 1) drives Menus, RangeDevelopment (rangesData.ts,
 * menuId 1) and Recipes > Usage (recipeUsageData.ts) so all three areas stay consistent.
 *
 * Dishes carry only { recipeId, price } (see MenuDish) - display name/category/allergens are
 * resolved at render time against the RecipeV2 catalogue (full recipes first, falling back to
 * catalogue summaries), never duplicated here.
 *
 * Fields with no equivalent in the source JSON (servicePeriods, standaloneItems, sites,
 * versionHistory, publishing channels) are synthesised to be consistent with the JSON's
 * "Lunch" service, weekly 5-day cycle and 6-site trial (see `trial`, sourced directly from
 * menus[0].trial). status is MenuStatus.Scheduled because the source status is "Approved for
 * trial" and the trial start date (2026-09-14) is still in the future relative to this data's
 * "as of" date. */
export const menus: Menu[] = [
  {
    id: 1,
    name: 'Autumn at the Table',
    description: `${'The British Kitchen'} - ${'A celebration of the British harvest, bringing together comforting seasonal flavours, thoughtfully sourced ingredients and modern kitchen craft.'}`,
    status: MenuStatus.Scheduled,
    menuType: MenuType.Lunch,
    menuCycle: MenuCycle.Weekly,
    servicePeriods: [
      { name: 'Lunch', startTime: '12:00', endTime: '14:00', days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'] },
    ],
    courses: [
      { name: 'Kitchen Counter', dishes: [{ recipeId: 601, price: 7.95 }, { recipeId: 602, price: 7.25 }, { recipeId: 622, price: 7.75 }, { recipeId: 623, price: 7.65 }, { recipeId: 607, price: 8.25 }, { recipeId: 608, price: 8.5 }, { recipeId: 603, price: 7.75 }, { recipeId: 610, price: 8.15 }, { recipeId: 612, price: 8.25 }, { recipeId: 613, price: 7.75 }] },
      { name: 'Plant-Forward Counter', dishes: [{ recipeId: 621, price: 6.95 }, { recipeId: 604, price: 7.15 }, { recipeId: 609, price: 7.25 }, { recipeId: 611, price: 6.95 }, { recipeId: 614, price: 7.15 }] },
      { name: 'Soup', dishes: [{ recipeId: 605, price: 4.5 }, { recipeId: 615, price: 4.5 }, { recipeId: 616, price: 4.5 }, { recipeId: 617, price: 4.75 }, { recipeId: 624, price: 4.5 }] },
      { name: 'Dessert', dishes: [{ recipeId: 606, price: 3.5 }, { recipeId: 618, price: 3.75 }, { recipeId: 619, price: 3.75 }, { recipeId: 620, price: 3.75 }, { recipeId: 625, price: 3.75 }] },
    ],
    dayStructure: [
      {
        day: 'Monday',
        courses: [
          { name: 'Kitchen Counter', dishes: [{ recipeId: 601, price: 7.95 }, { recipeId: 602, price: 7.25 }] },
          { name: 'Plant-Forward Counter', dishes: [{ recipeId: 621, price: 6.95 }] },
          { name: 'Soup', dishes: [{ recipeId: 605, price: 4.5 }] },
          { name: 'Dessert', dishes: [{ recipeId: 606, price: 3.5 }] },
        ],
      },
      {
        day: 'Tuesday',
        courses: [
          { name: 'Kitchen Counter', dishes: [{ recipeId: 622, price: 7.75 }, { recipeId: 623, price: 7.65 }] },
          { name: 'Plant-Forward Counter', dishes: [{ recipeId: 604, price: 7.15 }] },
          { name: 'Soup', dishes: [{ recipeId: 615, price: 4.5 }] },
          { name: 'Dessert', dishes: [{ recipeId: 618, price: 3.75 }] },
        ],
      },
      {
        day: 'Wednesday',
        courses: [
          { name: 'Kitchen Counter', dishes: [{ recipeId: 607, price: 8.25 }, { recipeId: 608, price: 8.5 }] },
          { name: 'Plant-Forward Counter', dishes: [{ recipeId: 609, price: 7.25 }] },
          { name: 'Soup', dishes: [{ recipeId: 616, price: 4.5 }] },
          { name: 'Dessert', dishes: [{ recipeId: 619, price: 3.75 }] },
        ],
      },
      {
        day: 'Thursday',
        courses: [
          { name: 'Kitchen Counter', dishes: [{ recipeId: 603, price: 7.75 }, { recipeId: 610, price: 8.15 }] },
          { name: 'Plant-Forward Counter', dishes: [{ recipeId: 611, price: 6.95 }] },
          { name: 'Soup', dishes: [{ recipeId: 617, price: 4.75 }] },
          { name: 'Dessert', dishes: [{ recipeId: 620, price: 3.75 }] },
        ],
      },
      {
        day: 'Friday',
        courses: [
          { name: 'Kitchen Counter', dishes: [{ recipeId: 612, price: 8.25 }, { recipeId: 613, price: 7.75 }] },
          { name: 'Plant-Forward Counter', dishes: [{ recipeId: 614, price: 7.15 }] },
          { name: 'Soup', dishes: [{ recipeId: 624, price: 4.5 }] },
          { name: 'Dessert', dishes: [{ recipeId: 625, price: 3.75 }] },
        ],
      },
    ],
    standaloneItems: [
      { itemNumber: 'ITM-BRK-BEV-01', description: 'Sparkling Elderflower Presse, 330ml', price: 2.25, category: 'Beverages' },
      { itemNumber: 'ITM-BRK-BEV-02', description: 'Still Water, 500ml', price: 1.1, category: 'Beverages' },
      { itemNumber: 'ITM-BRK-SID-01', description: 'Sourdough Roll and Butter', price: 1.5, category: 'Sides' },
    ],
    sites: [
      { siteName: 'Riverside Corporate Campus (BRK-01)', region: 'London' },
      { siteName: 'Northgate Business Park (BRK-02)', region: 'North West' },
      { siteName: 'Midland Technology Quarter (BRK-03)', region: 'Midlands' },
      { siteName: 'Harbourside Office Village (BRK-04)', region: 'South West' },
      { siteName: 'Castlefield Financial Centre (BRK-05)', region: 'Scotland' },
      { siteName: 'Eastfield Innovation Hub (BRK-06)', region: 'East of England' },
    ],
    targetMarginPercent: 68,
    versionHistory: [
      { version: 1, date: '2026-07-14', changeSummary: 'Initial Autumn at the Table menu structure and recipe selection created for range sign-off.' },
      { version: 2, date: '2026-08-25', changeSummary: 'Portions and pricing refined ahead of the 6-site trial; Plant-Forward Counter dishes rebalanced to meet the range\'s 40% plant-based target.' },
    ],
    publishing: {
      publishStatus: MenuStatus.Scheduled,
      channels: ['Site digital menu boards', 'Printed menu cards', 'Staff intranet menu page'],
    },
    trial: {
      siteCount: 6,
      startDate: '2026-09-14',
      endDate: '2026-10-09',
      reviewDate: '2026-10-12',
    },
  },
];
